import {
  ContextType,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import { isPdfPageRendered } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { FabricContext } from '@context/FabricContext';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

type UsePdfFabricCanvasParams = {
  fabricContext: ContextType<typeof FabricContext>;
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  contentRef: RefObject<HTMLDivElement>;
  canvasContainerRef: RefObject<HTMLDivElement>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  activePageNumber: number;
  zoomFactor: number;
  isPageSizeReady: boolean;
  selectedCommentId: string | null;
  pageRefsVersion: number;
  pagesReadyVersion: number;
  isMainCanvasVisible: boolean;
  selectComment: IAssetViewerStateContextValue['selectComment'];
  setCanvasMode: IAssetViewerStateContextValue['setCanvasMode'];
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
};

export function usePdfFabricCanvas({
  fabricContext,
  wrapperRef,
  contentRef,
  canvasContainerRef,
  pageRefsMap,
  activePageNumber,
  zoomFactor,
  isPageSizeReady,
  selectedCommentId,
  pageRefsVersion,
  pagesReadyVersion,
  isMainCanvasVisible,
  selectComment,
  setCanvasMode,
  setDocumentHighlight
}: UsePdfFabricCanvasParams) {
  const setFabricCanvasVisible = useCallback(
    (visible: boolean) => {
      const canvasContainer = canvasContainerRef.current;
      if (!canvasContainer) return;
      canvasContainer.style.visibility = visible ? 'visible' : 'hidden';
      canvasContainer.style.pointerEvents = visible ? '' : 'none';
    },
    [canvasContainerRef]
  );

  const syncFabricCanvas = useCallback((): boolean => {
    const canvasContainer = canvasContainerRef?.current;
    const content = contentRef?.current;
    if (!canvasContainer || !content || !fabricContext.canvas) return false;

    const pageEl = pageRefsMap.current.get(activePageNumber);
    if (!pageEl || !isPdfPageRendered(pageEl)) return false;

    const targetEl =
      (pageEl.querySelector('.media_viewer_pdf-inner') as HTMLElement | null) ??
      pageEl;

    const rect = targetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    content.style.removeProperty('width');
    content.style.removeProperty('height');

    const contentRect = content.getBoundingClientRect();
    const left = rect.left - contentRect.left + rect.width / 2;
    const top = rect.top - contentRect.top + rect.height / 2;

    canvasContainer.style.left = `${left}px`;
    canvasContainer.style.top = `${top}px`;
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;

    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
    return true;
  }, [
    canvasContainerRef,
    contentRef,
    pageRefsMap,
    activePageNumber,
    zoomFactor,
    fabricContext.canvas,
    pageRefsVersion
  ]);

  const runCanvasLayout = useCallback(() => {
    if (!fabricContext.canvas) return;

    if (!isPageSizeReady || !isMainCanvasVisible) {
      setFabricCanvasVisible(false);
      return;
    }

    const synced = syncFabricCanvas();
    if (!synced) {
      setFabricCanvasVisible(false);
      return;
    }

    setFabricCanvasVisible(true);
    fabricContext.canvas.requestRenderAll();
  }, [
    isPageSizeReady,
    isMainCanvasVisible,
    fabricContext.canvas,
    syncFabricCanvas,
    setFabricCanvasVisible
  ]);

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      syncFabricCanvas();
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [
      fabricContext.canvas,
      syncFabricCanvas,
      onClearHighlightObjects,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    ]
  );

  useEffect(() => {
    runCanvasLayout();

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.addEventListener('scroll', runCanvasLayout, {
      passive: true
    });
    window.addEventListener('resize', runCanvasLayout);

    return () => {
      viewport?.removeEventListener('scroll', runCanvasLayout);
      window.removeEventListener('resize', runCanvasLayout);
    };
  }, [wrapperRef, runCanvasLayout]);

  useLayoutEffect(() => {
    runCanvasLayout();
  }, [
    selectedCommentId,
    activePageNumber,
    pageRefsVersion,
    pagesReadyVersion,
    zoomFactor,
    isMainCanvasVisible,
    runCanvasLayout
  ]);

  return {
    setFabricCanvasVisible,
    syncFabricCanvas,
    runCanvasLayout,
    onClearHighlightObjects,
    renderTextHighlights
  };
}
